/**
 * API contract with istari-server.
 *
 * Wave 2 shapes are reconciled against the live server DTOs
 * (`crates/istari-server/src/routes/models.rs` + `istari-core` serde types) —
 * the server is canonical. Wave 3 adds the pinned MEP generation contract
 * (POST /api/models/{id}/generate + /api/mep-systems/* reads + findings
 * overrides). Field names are snake_case verbatim as the server serializes
 * them; nullable fields are `null`, never omitted, on the web type.
 */
import type { PlanPoint } from "../viewer/ifc/types";

/** GET/POST /api/projects */
export interface ApiProject {
  id: string;
  name: string;
  description: string;
  created_at: number;
  updated_at: number;
}

/** POST /api/projects/{id}/models (multipart .ifc) */
export interface ModelCreated {
  model_id: string;
}

/**
 * Occupancy vocabulary is fetched at runtime from `GET /api/vocab/occupancy`
 * and cached in the store. The single source of truth is
 * `crates/istari-core/src/occupancy.rs`. See ADR 0010 for the rationale (and
 * for why we deliberately don't codegen this into a TS literal-union type).
 */
export type { OccupancyVocabEntry } from "./client";

export const EXCLUSION_TIERS = ["none", "exclusion", "no_go"] as const;
/** Exclusion severity tiers (spec §2 item 1: soft avoid vs hard block). */
export type ExclusionTier = (typeof EXCLUSION_TIERS)[number];

/** One space row from GET /api/models/{id}/spaces (server `SpaceDto`). */
export interface ApiSpace {
  /** Server-side space id — used in the PATCH path. */
  id: string;
  /** IFC express ID linking back to `SpaceSummary.id`. */
  ifc_id: number | null;
  /** IFC GlobalId — stable across re-exports. */
  global_id: string | null;
  name: string;
  long_name: string | null;
  /** IFC express ID of the containing storey (server: `storey_ifc_id`). */
  storey_ifc_id: number | null;
  storey_name: string | null;
  area_m2: number | null;
  /** Plan footprint polygon (meters); falls back to the local ModelSummary. */
  footprint: PlanPoint[] | null;
  /** Classified occupancy (heuristic/LLM, cached) — advisory. */
  occupancy: string | null;
  /** Engineer override; wins over `occupancy` when set. */
  occupancy_override: string | null;
  /** Server-computed occupancy after applying the override. */
  effective_occupancy: string | null;
  /** Effective provenance of the occupancy (T1). Advisory only. */
  occupancy_source?: "inferred" | "conflicting" | "struct_gap_fill" | "confirmed" | null;
  /** Conditioning classification computed at parse time (ENG-15038). null until re-parse. */
  conditioning_class:
    | { kind: "conditioned" }
    | { kind: "non_conditioned"; reason: string }
    | { kind: "mep_serving"; reason: string }
    | null;
  is_exit: boolean;
  exclusion: ExclusionTier;
  /** Ambient sound level in dBA for the audibility screen, null = default. */
  ambient_dba: number | null;
  /** Per-space finish ceiling height in meters; null falls back to storey default. */
  ceiling_height_m: number | null;
  /** Which extraction path produced `ceiling_height_m` (e.g. `FinishCeilingHeight`). */
  ceiling_height_source: string | null;
  /**
   * Per-ruleset occupancy subtypes assigned to this space (ENG-14678 / ADR
   * 0011). `{"ashrae-170-2021": "aii"}` flags this `patient_room` as an AII
   * room — the matcher prefers a Table 7.1 row keyed on that subtype.
   */
  subtypes: Record<string, string>;
  /**
   * Subtypes that were dropped on the most recent PATCH because the new
   * effective occupancy no longer matches their declared parent_occupancy.
   * Empty outside of a PATCH response. The wizard can use this to surface a
   * "we cleared X — please re-pick" toast.
   */
  subtypes_dropped?: [string, string][];
}

/** PATCH /api/models/{id}/spaces/{space_id} — only present keys are applied. */
export interface SpacePatch {
  occupancy_override?: string | null;
  is_exit?: boolean;
  exclusion?: ExclusionTier;
  ambient_dba?: number | null;
  /**
   * Replace the space's subtype map. Pass an empty map to clear all
   * subtypes; omit the field entirely to leave it unchanged. Validated
   * server-side against the active corpus.
   */
  subtypes?: Record<string, string>;
}

/** One storey row from GET /api/models/{id}/storeys (server `StoreyDto`). */
export interface ApiStorey {
  /** Server-side storey id — matches device/terminal `storey_id` rows. */
  id: string;
  /** IFC express ID — equals the Fragments local ID for viewer isolation. */
  ifc_id: number;
  global_id: string | null;
  name: string;
  elevation: number | null;
}

/** POST /api/models/{id}/spaces/auto-assign body (demo helper; all optional). */
export interface AutoAssignOptions {
  /** Classify spaces whose effective occupancy is empty (default true). */
  occupancy?: boolean;
  /** Mark exits via egress keywords + per-storey fallback (default true). */
  exits?: boolean;
  /** Echoed back for API compatibility; occupancy assignment is deterministic. */
  seed?: number;
}

/** Response of the auto-assign demo helper. */
export interface AutoAssignResult {
  seed: number;
  occupancy_assigned: number;
  exits_marked: number;
  /** All spaces after the writes (replaces the local space list). */
  spaces: ApiSpace[];
  /** Readiness after the writes (replaces the local report). */
  readiness: ReadinessReport;
}

export const READINESS_STATUSES = ["found", "partial", "missing"] as const;
export type ReadinessStatus = (typeof READINESS_STATUSES)[number];

/**
 * One capability line in the model-readiness report
 * (`istari_core::readiness::Capability`).
 */
export interface ReadinessCapability {
  /**
   * Stable key (server `CapabilityKey`): `spaces_present`, `space_footprints`,
   * `occupancy_classified`, `exits_marked`, `ceiling_grids`, `raceways`.
   */
  key: string;
  status: ReadinessStatus;
  /** Confidence in [0, 1]. */
  confidence: number;
  /** Human-readable note, e.g. "no exits marked". */
  detail: string;
}

/**
 * Whether one generation kind is enabled, and what blocks it
 * (`istari_core::readiness::GenerationGate`).
 */
export interface GenerationGate {
  /** Server `GenerationKind`: `fire_alarm` | `hvac`. */
  kind: string;
  enabled: boolean;
  /** Capability keys currently `missing` among this kind's requirements. */
  blocked_by: string[];
  /**
   * Failing model-quality check names blocking this gate (ENG-17381), e.g.
   * `contract_version`, `room_boundaries`, or the `quality_not_evaluated`
   * fallback token when the model's quality hasn't produced per-check
   * evidence yet but is still not generation-eligible. Model-quality is a
   * whole-model verdict, so this list is identical across every gate in one
   * report — unlike `blocked_by`. Empty when quality is generation-eligible.
   * Optional for compatibility with hand-written fixtures predating this
   * field; the server always sends it.
   */
  blocked_by_quality?: string[];
}

/**
 * Whole-model exclusion counts (`istari_core::readiness::ExclusionCounts`,
 * ENG-17381 Phase 2 disclosure gate). Populated from the model's
 * `ModelQualityReport` whenever one has been evaluated — in BOTH the
 * `observe` and `enforce` quality-gate rollout modes (advisory counts must
 * not ride on that switch) — so a model whose quality has never been
 * evaluated (or an older server / a boon-produced payload predating this
 * field) is the only case that reports zero here. Advisory only: it never
 * gates `GenerationGate.enabled`. The counts mirror
 * `ModelQualityReport.excluded_spaces`/`excluded_openings` lengths — this
 * type deliberately carries only the counts, not the lists themselves.
 * Exposed on the wire and typed here on the client; no `web/src/` UI renders
 * it yet (rendering is a tracked follow-up).
 */
export interface ExclusionCounts {
  spaces: number;
  openings: number;
}

/** GET /api/models/{id}/readiness (`istari_core::ReadinessReport`). */
export interface ReadinessReport {
  capabilities: ReadinessCapability[];
  generation: GenerationGate[];
  /** Optional for compatibility with older servers/fixtures; the current
   * server always sends it. Absent guards to `{ spaces: 0, openings: 0 }`. */
  exclusions?: ExclusionCounts;
}

/** One space in the re-upload diff (`istari_core::model_diff::DiffEntry`). */
export interface DiffEntry {
  /** Stable identity key (`GlobalId`, else `ifc:<express id>`). */
  key: string;
  name: string;
  /** Footprint hash in the previous model (absent for `added`). */
  prev_hash: string | null;
  /** Footprint hash in the new model (absent for `removed`). */
  new_hash: string | null;
}

/**
 * GET /api/models/{id}/diff — re-upload versioning. The server wraps the
 * core `ModelDiff` under a `diff` key alongside the version ids.
 */
export interface ModelDiff {
  model_id: string;
  prev_model_id: string;
  diff: {
    added: DiffEntry[];
    removed: DiffEntry[];
    changed: DiffEntry[];
    /** Identity keys present in both versions with identical footprints. */
    unchanged: string[];
  };
}

// ---------------------------------------------------------------------------
// Wave 3 — MEP generation (pinned contract)
// ---------------------------------------------------------------------------

// Mechanical is HVAC EXPANDED — "hvac" is the single division-23 discipline
// and covers ductwork AND equipment placement/sizing. There is deliberately no
// "mechanical" entry: the server has no such generate discipline.
export const MEP_DISCIPLINES = ["fire_alarm", "hvac", "electrical", "structural"] as const;
export type MepDiscipline = (typeof MEP_DISCIPLINES)[number];

/**
 * Structural engine settings. Every field is an istari ENGINEERING DEFAULT —
 * girder direction, infill spacing, slab thickness — and none is a code value.
 * They are exposed so an engineer can disagree with an assumption rather than
 * discover it later.
 */
export interface StructuralSettings {
  /** `auto` picks the axis with the smaller mean bay spacing. */
  girder_axis?: "auto" | "x" | "y";
  max_infill_beam_spacing_m?: number;
  default_slab_thickness_m?: number;
  place_infill_beams?: boolean;
  place_footings?: boolean;
  place_lateral_placeholders?: boolean;
}

/** POST /api/models/{id}/generate request body. */
export interface GenerateRequest {
  discipline: MepDiscipline;
  /** Omit to let the server roll a fresh seed (procedural variation). */
  seed?: number;
  /** Discipline-specific settings (HvacSettings for `hvac`, ElectricalSettings for `electrical`). */
  settings?: HvacSettings | ElectricalSettings | Record<string, unknown>;
}

/** 202 response from POST /api/models/{id}/generate. */
export interface GenerateAccepted {
  job_id: string;
  mep_system_id: string;
  /** The seed actually used — rolled server-side when absent in the request. */
  seed: number;
}

/** GET /api/mep-systems/{id} (also the rows of GET /api/models/{id}/mep-systems). */
export interface MepSystem {
  id: string;
  model_id: string;
  discipline: string;
  status: string;
  seed: number;
  ruleset_hash: string | null;
  generated_at: number | null;
}

/** One device from GET /api/mep-systems/{id}/devices. */
export interface MepDevice {
  id: string;
  /** Catalog device kind, e.g. `smoke_detector`, `strobe`, `pull_station`. */
  kind: string;
  catalog_item_id: string | null;
  storey_id: string | null;
  space_id: string | null;
  x_m: number;
  y_m: number;
  z_m: number;
  /** Mounting, e.g. `ceiling` | `wall`. */
  mount: string | null;
  candela: number | null;
  dba_at_10ft: number | null;
  circuit_id: string | null;
  zone_id: string | null;
  /** `generated` | `user` provenance. */
  origin: string | null;
}

/** One zone from GET /api/mep-systems/{id}/zones. */
export interface MepZone {
  id: string;
  storey_id: string | null;
  space_ids: string[];
  area_m2: number | null;
}

/** One circuit/loop from GET /api/mep-systems/{id}/circuits. */
export interface MepCircuit {
  id: string;
  /** `slc` | `nac` (US vocabulary per NFPA 72). */
  kind: string;
  device_ids: string[];
  wire_length_m: number | null;
  worst_voltage_v: number | null;
  /** Capacity + voltage-drop checks passed. */
  ok: boolean;
}

// ---------------------------------------------------------------------------
// Wave 4 — HVAC ductwork (pinned contract)
// ---------------------------------------------------------------------------

export const DUCT_SYSTEMS = ["supply", "return"] as const;
export type DuctSystem = (typeof DUCT_SYSTEMS)[number];

export const DUCT_SHAPES = ["round", "rect"] as const;
export type DuctShape = (typeof DUCT_SHAPES)[number];

/**
 * Settings for `POST /api/models/{id}/generate` with `discipline: "hvac"`
 * (pinned contract `settings` object — keys are optional; the server applies
 * defaults for absent keys).
 */
export interface HvacSettings {
  /** Apply ASHRAE 170 healthcare ventilation rules (ACH-driven airflows). */
  healthcare?: boolean;
  /** Available ceiling-plenum depth for duct routing, meters. */
  plenum_depth_m?: number;
  /** Space ids to use as vertical riser locations. */
  riser_space_ids?: string[];
}

/**
 * Electrical generation settings (sent with `discipline: "electrical"`). All
 * fields optional — the server defaults 208Y/120V, 3φ, and auto-sizes the
 * service ampacity to the computed NEC Article 220 demand when omitted.
 */
export interface ElectricalSettings {
  /** Service line-to-line voltage (e.g. 208 or 480). */
  service_voltage?: number;
  /** Service phase count (1 or 3). */
  service_phase?: number;
  /**
   * Service ampacity (A). Omit to auto-size to the computed demand; set to pin
   * a specific main frame (a genuinely undersized value still trips the flag).
   */
  service_ampacity?: number;
  /** Per-storey electrical-room hint: `storey_id` → `space_id`. */
  electrical_rooms?: Record<string, string>;
  /**
   * Opt-in grounding & bonding stage (NEC Article 250: GEC/MBJ/bonding/grounded
   * conductors + the grounding-electrode system). Omit or `false` ⇒ the
   * grounding stage does not run and the Output serializes byte-identically to
   * a pre-grounding design.
   */
  ground_system?: boolean;
  /**
   * Opt-in emergency & standby power stage (NEC Article 517/700: the emergency
   * source enrichment + ATSes + the essential-electrical-system branch
   * classification). Omit or `false` ⇒ the emergency stage does not run and the
   * Output serializes byte-identically to a pre-emergency design.
   */
  emergency_power?: boolean;
  /**
   * Opt-in voltage-drop analysis stage (NEC 210.19(A) IN / 215.2(A)(1) IN: the
   * branch 3% / combined 5% screening study over sized conductors). Omit or
   * `false` ⇒ the stage does not run and the Output serializes byte-identically
   * to a design without a voltage-drop study.
   */
  voltage_drop_analysis?: boolean;
  /**
   * Opt-in Title 24 §130.1(b) multi-level lighting-control screening (enclosed
   * spaces ≥ 100 ft² with installed LPD > 0.5 W/ft² require multi-level control).
   * Omit or `false` ⇒ the stage does not run and the Output serializes
   * byte-identically to a design without the multi-level check.
   */
  lighting_controls?: boolean;
  /** Opt-in NFPA 101 §7.9 / IBC §1008 egress-path illumination screening (avg ≥1 fc). Byte-identical when off. */
  egress_illumination?: boolean;
  /**
   * Opt-in Title 24 §130.1(e) demand-responsive lighting-control screening
   * (buildings whose total installed interior lighting power is ≥ 4,000 W require
   * demand-responsive controls, via §110.12(c)). Its OWN flag — NOT shared with
   * `lighting_controls`. Omit or `false` ⇒ the stage does not run and the Output
   * serializes byte-identically to a design without the demand-responsive check.
   */
  demand_response_controls?: boolean;
  /**
   * Opt-in NEC Article 430/440 motor-circuit sizing screening (conductor/OCPD/
   * overload from the FLC/FLA split). Its OWN flag. Omit or `false` ⇒ the stage
   * does not run and the Output serializes byte-identically. A production no-op
   * even when on until HVAC connected loads carry motor fields.
   */
  motor_circuits?: boolean;
  /**
   * Opt-in Title 24 §130.1(d) automatic-daylighting-control screening (sidelit/
   * skylit daylit zones whose general-lighting power meets the per-zone threshold
   * require automatic daylighting controls). Its OWN flag — NOT shared with
   * `lighting_controls`. Omit or `false` ⇒ the stage does not run and the Output
   * serializes byte-identically to a design without the daylighting check.
   */
  daylighting_controls?: boolean;
}

// ---------------------------------------------------------------------------
// Electrical raceway/conduit routing (opt-in Output; ENG conduit-raceway).
// These live on the raw discipline Output (GET /api/mep-systems/{id}/output),
// not on a dedicated list route — they are `skip_serializing_if` empty, so a
// non-conduit electrical Output omits them entirely (fields are optional).
// Field names are the engine serde names verbatim (crates/istari-engines/src/
// electrical/output.rs); `glam::DVec2` serializes as a 2-tuple `[x, y]`.
// ---------------------------------------------------------------------------

/** One routed, fill-sized raceway segment from the electrical Output. */
export interface Conduit {
  id: string;
  /** Server storey id of the route's storey — the basis for storey filtering
   * and the per-storey world-z lift (z_m is storey-local). */
  storey_id: string;
  /** Circuits whose conductors share this segment. */
  circuit_ids: string[];
  /** Raceway kind: `emt` | `rmc` | `imc` | `pvc_sch40` | `cable_tray`. */
  kind: string;
  /** Trade size (inches). */
  trade_size_in: number;
  /** Conductor fill (percent). */
  fill_pct: number;
  /** Manhattan centerline in plan meters, `[[x, y], ...]`, at height `z_m`. */
  polyline: [number, number][];
  /** Centerline height (meters, storey-local). */
  z_m: number;
  /** Centerline + drops + routing slack (meters). */
  length_m: number;
}

/** One junction / pull box at a raceway node from the electrical Output. */
export interface PullBox {
  id: string;
  /** Server storey id — the basis for storey filtering and the per-storey
   * world-z lift (z_m is storey-local). */
  storey_id: string;
  /** Box position in plan meters, `[x, y]`. */
  position: [number, number];
  /** Box height (meters, storey-local). */
  z_m: number;
  /** `314.16` (box-volume, small conductors) | `314.28` (pull geometry, ≥#4). */
  sizing_basis: string;
  /** 314.28 minimum LINEAR box dimension (inches); `0.0` for a 314.16 box
   * (whose sizing is a cubic-inch volume — see `box_volume_in3`). */
  min_dim_in: number;
  /** 314.16 required box VOLUME (cubic inches); omitted for a 314.28 box.
   * Kept distinct so the in³ volume is never read as a linear inch dimension. */
  box_volume_in3?: number;
}

/** One bend / box fitting on a raceway run from the electrical Output. */
export interface RacewayFitting {
  /** Raceway this fitting sits on. */
  conduit_id: string;
  /** `bend` | `box`. */
  kind: string;
  /** Fitting position in plan meters, `[x, y]`. */
  position: [number, number];
}

// ---------------------------------------------------------------------------
// Electrical grounding & bonding (opt-in Output; NEC Article 250). Like the
// raceway arrays, these live on the raw discipline Output and are
// `skip_serializing_if` empty/None — a non-grounding electrical Output omits
// them entirely. Field names are the engine serde names verbatim
// (crates/istari-engines/src/electrical/output.rs).
// ---------------------------------------------------------------------------

/** One sized grounding / bonding conductor from the electrical Output. */
export interface GroundingConductor {
  id: string;
  /** `gec` | `mbj` | `bonding_supply` | `bonding_load` | `grounded`. */
  kind: string;
  /** Sized conductor size (AWG/kcmil token). */
  size_awg: string;
  /** Upstream node bonded from (e.g. `main`, a panel id). */
  from_node: string;
  /** Downstream node bonded to (e.g. an electrode id, a panel id). */
  to_node: string;
  /** Sizing-basis citation (e.g. `NEC 250.66`) — section numbers only. */
  basis_citation: string;
}

/** One grounding electrode + its GEC size from the electrical Output. */
export interface GroundingElectrode {
  id: string;
  /** `ufer` | `rod` | `water_pipe`. */
  kind: string;
  /** GEC size (AWG/kcmil token) serving this electrode. */
  gec_size_awg: string;
}

/** One conductor row of the grounding schedule (NEC Article 250). */
export interface GroundingScheduleConductorRow {
  /** Conductor role, matching `GroundingConductor.kind`. */
  kind: string;
  /** Human-facing role label (e.g. "Grounding electrode conductor (GEC)"). */
  label: string;
  /** Sized conductor size (AWG/kcmil). */
  size_awg: string;
  /** Bonds from node id. */
  from_node: string;
  /** Bonds to node id. */
  to_node: string;
  /** Section citation (e.g. `NEC 250.66`). */
  citation: string;
}

/** One electrode row of the grounding schedule (NEC 250.52). */
export interface GroundingScheduleElectrodeRow {
  /** Electrode kind (`ufer` | `rod` | `water_pipe`). */
  kind: string;
  /** Human-facing electrode label (e.g. "Concrete-encased electrode (Ufer)"). */
  label: string;
  /** GEC size (AWG/kcmil) serving this electrode. */
  gec_size_awg: string;
  /** Section citation for the electrode (e.g. `NEC 250.52(A)(3)`). */
  citation: string;
}

/**
 * The grounding schedule (NEC Article 250): the practitioner-facing table of
 * sized grounding/bonding conductors + the grounding-electrode system. Present
 * only when the opt-in grounding stage ran (`grounding_schedule` is `None`
 * otherwise so grounding-off outputs serialize byte-identically).
 */
export interface GroundingSchedule {
  conductor_rows: GroundingScheduleConductorRow[];
  electrode_rows: GroundingScheduleElectrodeRow[];
}

// ---------------------------------------------------------------------------
// Electrical emergency & standby power (opt-in Output; NEC Article 517/700).
// Like the grounding arrays, these live on the raw discipline Output and are
// `skip_serializing_if` empty/None — a non-emergency electrical Output omits
// them entirely. Field names are the engine serde names verbatim
// (crates/istari-engines/src/electrical/output.rs). The per-element branch
// classification is a HEURISTIC screening estimate — a licensed engineer must
// confirm each branch assignment (surfaced in the UI, never hidden).
// ---------------------------------------------------------------------------

/** NEC 517.30 branch class token (`EmergencyBranch`, snake_case serde). */
export type EmergencyBranch = "life_safety" | "critical" | "equipment";

/**
 * The enriched emergency power source (NEC 700/517). The four enriched fields
 * are populated only by the opt-in enrichment stage; `capacity_va` is the
 * connected-load screening sum.
 */
export interface EmergencySource {
  id: string;
  circuit_ids: string[];
  /** Connected-load screening sum (VA). */
  capacity_va?: number;
  /** `generator` | `inverter` | `ups`; absent on the always-on build path. */
  source_kind?: string;
  /** Screening source ampacity (A). */
  ampacity_a?: number;
  /** `emergency` | `legally_required_standby` | `optional_standby`. */
  system_class?: string;
  /** Screening minimum runtime (minutes). */
  duration_min?: number;
}

/** One automatic transfer switch (NEC 700.5/517.31) from the electrical Output. */
export interface Ats {
  id: string;
  /** Upstream node (e.g. `emergency-source`, `main`). */
  from_node: string;
  /** Downstream node (e.g. a panel id). */
  to_node: string;
  /** Screening transfer ampacity (A). */
  ampacity_a: number;
  /** Emergency branch this ATS serves; absent when unclassified. */
  branch?: EmergencyBranch;
}

/** One row of the emergency-system schedule (NEC Article 517/700). */
export interface EmergencyScheduleRow {
  /** Row kind: `source` | `ats` | `branch`. */
  kind: string;
  /** Human-facing label (e.g. "Life-Safety branch"). */
  label: string;
  /** Sized quantity text (e.g. `100 A`, `150 VA`). */
  size: string;
  /** Section citation (e.g. `NEC 517.31`) — section numbers only. */
  citation: string;
  /** Per-branch transfer mode (`10s_auto` | `delayed_auto`); absent otherwise. */
  transfer_mode?: string;
}

/** The emergency-system schedule table (NEC Article 517/700). */
export interface EmergencySchedule {
  rows: EmergencyScheduleRow[];
}

/**
 * The emergency essential-electrical-system rollup + schedule (NEC Article
 * 517/700). Present only when the opt-in enrichment stage ran (`emergency_system`
 * is `None` otherwise so emergency-off outputs serialize byte-identically).
 */
export interface EmergencySystem {
  /** `generator` | `inverter` | `ups`. */
  source_kind: string;
  /** Screening source ampacity (A). */
  ampacity_a: number;
  /** `emergency` | `legally_required_standby` | `optional_standby`. */
  system_class: string;
  /** Screening minimum runtime (minutes). */
  duration_min: number;
  /** Connected VA per branch, keyed by the snake_case branch token. */
  branch_totals_va: Record<string, number>;
  /** The emergency-system schedule table. */
  schedule: EmergencySchedule;
}

// ---------------------------------------------------------------------------
// Electrical voltage-drop study (opt-in Output; NEC 210.19(A) IN / 215.2(A)(1)
// IN). Like the grounding/emergency sections, this lives on the raw discipline
// Output and is `skip_serializing_if` None — a design without the opt-in
// voltage-drop stage omits it entirely. Field names are the engine serde names
// verbatim (crates/istari-engines/src/electrical/output.rs). The IN thresholds
// are informational recommendations (branch 3% / combined 5%), not mandatory
// limits — feeder rows are informational-only (no pass/fail verdict).
// ---------------------------------------------------------------------------

/** One voltage-drop study row (branch / feeder / combined). */
export interface VoltageDropRow {
  /** The element id (circuit id for a branch, panel/feeder id otherwise). */
  id: string;
  /** Row kind: `branch` | `feeder` | `combined`. */
  kind: "branch" | "feeder" | "combined";
  /** Nominal circuit voltage (V). */
  voltage: number;
  /** Load current (A). */
  amps: number;
  /** One-way run length (ft). */
  length_ft: number;
  /** Effective impedance (Ω per 1000 ft) at the row's power factor. */
  zeff_per_1000ft: number;
  /** AC resistance (Ω per 1000 ft) — NEC Chapter 9 Table 9. */
  r_per_1000ft: number;
  /** Reactance (Ω per 1000 ft) — NEC Chapter 9 Table 9. */
  x_per_1000ft: number;
  /** Power factor used for the effective-impedance combination. */
  power_factor: number;
  /** Computed voltage drop (V). */
  volts: number;
  /** Computed voltage drop (percent of nominal). */
  percent: number;
  /** IN recommendation threshold (percent): 3 (branch) / 5 (combined). */
  threshold_pct: number;
  /**
   * `true` when the row's percent exceeds its threshold, `false` when within;
   * `null`/absent for informational feeder rows (no pass/fail verdict).
   */
  exceeded?: boolean | null;
  /** Section citation (e.g. `NEC 210.19(A) IN`) — section numbers only. */
  citation: string;
}

/**
 * The voltage-drop study (NEC 210.19(A) IN / 215.2(A)(1) IN): the screening
 * table of per-segment voltage drop vs the informational recommendation. Present
 * only when the opt-in voltage-drop stage ran (`voltage_drop_study` is `None`
 * otherwise so study-off outputs serialize byte-identically).
 */
export interface VoltageDropStudy {
  rows: VoltageDropRow[];
  /** Human-facing basis line (thresholds + citations). */
  basis: string;
}

/**
 * The subset of the raw electrical `Output` the viewer's raceway layer + the
 * results view read (GET /api/mep-systems/{id}/output). All arrays are
 * optional: the server omits them from an Output that lacks the opt-in raceway
 * / grounding stages (`skip_serializing_if` empty/None).
 */
export interface ElectricalOutput {
  conduits?: Conduit[];
  pull_boxes?: PullBox[];
  raceway_fittings?: RacewayFitting[];
  /** Sized grounding/bonding conductors (NEC Article 250); empty when off. */
  grounding_conductors?: GroundingConductor[];
  /** Grounding electrodes + GEC sizes (NEC 250.52/250.66); empty when off. */
  grounding_electrodes?: GroundingElectrode[];
  /** Grounding schedule table; absent when the grounding stage did not run. */
  grounding_schedule?: GroundingSchedule | null;
  /** Enriched emergency source (NEC 700/517); absent when off. */
  emergency_source?: EmergencySource | null;
  /**
   * NEC 517.30 branch classification keyed by NAMESPACED element id
   * (`circuit:<id>` / `exit_sign:<id>` / `hvac_load:<id>`) → branch token.
   * Empty when the emergency stage did not run. HEURISTIC — engineer confirms.
   */
  emergency_branch?: Record<string, EmergencyBranch>;
  /** Automatic transfer switches (NEC 700.5/517.31); empty when off. */
  ats?: Ats[];
  /** Emergency system rollup + schedule; absent when the stage did not run. */
  emergency_system?: EmergencySystem | null;
  /** Voltage-drop study; absent when the opt-in voltage-drop stage did not run. */
  voltage_drop_study?: VoltageDropStudy | null;
}

export const MECHANICAL_KINDS = [
  "rooftop_unit",
  "air_handling_unit",
  "pump",
  "vav_terminal",
] as const;
/** Kind of mechanical equipment the mechanical engine places. */
export type MechanicalKind = (typeof MECHANICAL_KINDS)[number];

/**
 * One placed piece of mechanical equipment from the raw mechanical `Output`.
 * Capacity fields are populated per kind only (tons for RTU/AHU, gpm for pump,
 * cfm + inlet for VAV terminal); the rest are absent. `screening_only` is
 * always true — every capacity is an estimating-grade snap up the catalog
 * ladder, never a manufacturer selection.
 */
export interface MechanicalEquipment {
  id: string;
  kind: MechanicalKind;
  /** Human-facing tag (e.g. `"RTU-1"`). */
  tag?: string | null;
  catalog_id: string;
  space_id: string;
  storey_id: string;
  /** Plan position in metres, `[x, y]` (glam::DVec2 serializes as a 2-tuple). */
  position: [number, number];
  z_m: number;
  /** Estimating-grade cooling capacity (tons) — RTU/AHU only. */
  capacity_tons?: number | null;
  /** Estimating-grade flow capacity (gpm) — pump only. */
  capacity_gpm?: number | null;
  /** Design airflow (cfm) — VAV terminal only, delegated from ASHRAE 62.1 Vbz. */
  cfm?: number | null;
  /** Selected inlet size (in) — VAV terminal only. */
  inlet_in?: number | null;
  /** Whether the footprint + IMC §306 clearance fit inside the host space. */
  clearance_ok: boolean;
  screening_only: boolean;
  /**
   * Per-element lineage the engine attaches at construction. `citation` is the
   * METHOD source backing this element's sized capacity (ASHRAE 62.1 Table
   * 6.2.2.1 for a 62.1-derived VAV cfm, else the ASHRAE Handbook—Fundamentals
   * screening chapter) and is populated for EVERY sized element — the results
   * tables render it as the row-level basis beside each capacity.
   */
  provenance?: {
    why: string;
    source: string;
    action: string;
    airflow_method?: string | null;
    rule_id?: string | null;
    citation?: string | null;
  } | null;
}

/** Which basis produced a zone's design airflow. */
export type DesignAirflowMethod = "ventilation_rate" | "screening_default";

/**
 * One space's delegated ASHRAE 62.1 ventilation demand + the ASHRAE
 * Handbook—Fundamentals equipment-capacity screen. Both carry the rule id +
 * section citation they came from (citations are section numbers only).
 *
 * `ventilation_cfm` is `0` when the occupancy is NOT tabulated in Table
 * 6.2.2.1 — read `design_cfm`/`design_method` for what equipment was actually
 * sized from. A `0` ventilation figure never means "this zone needs no air".
 */
export interface SpaceLoadScreening {
  space_id: string;
  storey_id: string;
  ventilation_cfm: number;
  ventilation_rule_id: string;
  ventilation_citation: string;
  /** The airflow equipment was sized from (62.1 Vbz, else the Handbook screen). */
  design_cfm: number;
  design_method: DesignAirflowMethod;
  design_rule_id: string;
  design_citation: string;
  screening_cfm: number;
  screening_rule_id: string;
  screening_citation: string;
}

/** Bill-of-quantities rollup on the raw mechanical `Output`. */
export interface MechanicalBoq {
  equipment_counts: Record<string, number>;
  total_capacity_tons_by_kind: Record<string, number>;
  /**
   * Section citation backing each kind's rolled-up capacity, carried from the
   * contributing equipment's own provenance and keyed identically to
   * `total_capacity_tons_by_kind` — so a rolled-up sized value never renders
   * without its basis.
   */
  total_capacity_citation_by_kind?: Record<string, string>;
}

/**
 * The raw mechanical `Output` the results view reads
 * (GET /api/mep-systems/{id}/output for a `mechanical` system).
 */
export interface MechanicalOutput {
  equipment?: MechanicalEquipment[];
  load_screening?: SpaceLoadScreening[];
  boq?: MechanicalBoq | null;
}

export const DUCT_ROLES = ["supply_main", "branch", "return_main", "return_branch"] as const;
/** Topological role of a duct within its system tree. */
export type DuctRole = (typeof DUCT_ROLES)[number];

/** One duct run from GET /api/mep-systems/{id}/ducts. */
export interface MepDuct {
  id: string;
  /** Server storey id of the route's storey (per-storey trunk-and-branch). */
  storey_id: string | null;
  /** `supply` | `return`. */
  system: DuctSystem;
  /** Topological role within the system tree. */
  role: DuctRole;
  /** Polyline in plan meters: [[x, y, z], ...] (x east, y north, z up). */
  centerline: [number, number, number][];
  /** `round` | `rect`. */
  shape: DuctShape;
  /** Round duct diameter (meters); null for rect ducts. */
  diameter_m: number | null;
  /** Rect duct width (meters); null for round ducts. */
  width_m: number | null;
  /** Rect duct height (meters); null for round ducts. */
  height_m: number | null;
  /** Design airflow through the run. */
  cfm: number | null;
  /** Design velocity (feet per minute). */
  velocity_fpm: number | null;
}

/** One fitting from GET /api/mep-systems/{id}/fittings. */
export interface MepFitting {
  id: string;
  /** `elbow` | `tee` | `reducer` | `boot` | `fire_damper` | `smoke_damper` | `combination_damper`. */
  kind: string;
  x_m: number;
  y_m: number;
  z_m: number;
  /** Ducts this fitting joins. */
  duct_ids: string[];
}

/** One terminal from GET /api/mep-systems/{id}/terminals. */
export interface MepTerminal {
  id: string;
  /** `supply_diffuser` | `return_grille` | ... */
  kind: string;
  catalog_item_id: string | null;
  space_id: string | null;
  storey_id: string | null;
  x_m: number;
  y_m: number;
  z_m: number;
  /** Neck size, inches. */
  neck_in: number | null;
  cfm: number | null;
}

/**
 * Snake-case discriminator for `MepEquipment.kind`. v1 ships only the AHU
 * placeholder stub; widen this union when more equipment kinds land.
 */
export type EquipmentKind = "air_handling_unit";

/**
 * One placed piece of HVAC equipment from GET
 * `/api/mep-systems/{id}/equipment`. v1 carries only the geometric anchor —
 * sizing/airflow live on connected ducts. `screening_only` flags placeholder
 * rows the UI should pair with a screening-only finding.
 */
export interface MepEquipment {
  id: string;
  /** Snake-case `EquipmentKind` (e.g. `"air_handling_unit"`). */
  kind: EquipmentKind;
  catalog_item_id: string;
  space_id: string;
  storey_id: string;
  x_m: number;
  y_m: number;
  z_m: number;
  /** True for placeholder/screening-only equipment; pairs with a finding. */
  screening_only: boolean;
}

/**
 * One per-space airflow requirement from GET /api/mep-systems/{id}/airflows.
 * `method` names the screening-level basis (e.g. ventilation-rate procedure
 * vs ACH); `citation` is section numbers only — the ASHRAE trust pattern.
 */
export interface MepAirflow {
  space_id: string;
  cfm: number;
  method: string;
  citation: FindingCitation | null;
}

// ---------------------------------------------------------------------------
// Wave 5 — deliverable exports + codes monitor (scraper, monitor-only)
// ---------------------------------------------------------------------------
//
// Export endpoints (A5 contract):
//   GET /api/mep-systems/{id}/riser.svg    — riser diagram (fire-alarm only)
//   GET /api/mep-systems/{id}/report.pdf   — PDF report (findings + overrides)
//   GET /api/models/{id}/placements.dxf    — DXF of floor placements
//
// Scraper endpoints (server canonical — istari-server/src/routes/scraper.rs):
//   GET    /api/scraper/sources                -> bare [source, ...]
//   POST   /api/scraper/sources                -> 201 source row
//   POST   /api/scraper/run                    -> 202 job row (global run of
//                                                 all sources; job SSE follows)
//   GET    /api/scraper/review-items           -> bare [item, ...]
//   PATCH  /api/scraper/review-items/{id}      -> updated review item row
//
// Sources are list + create only — the server exposes no DELETE and no
// per-source run. The scraper is monitor-only (spec §2 item 10): it never
// writes rules. Every detected change lands in the human review queue below.

/** One monitored jurisdiction page from GET /api/scraper/sources. */
export interface ScraperSource {
  id: string;
  /** Jurisdiction id (e.g. "santa-clara-county"). */
  jurisdiction: string;
  /** Allowlisted page URL (Municode/amlegal/ecode360 or a .gov host). */
  url: string;
  /** Optional CSS selector limiting the watched region of the page. */
  css_selector_scope: string | null;
  /** Content hash from the last check (change-detection basis). */
  last_hash: string | null;
  /** Epoch ms of the last completed check; null = never checked. */
  last_checked_ms: number | null;
  etag: string | null;
  last_modified: string | null;
  created_at: number;
  updated_at: number;
}

/** POST /api/scraper/sources request body. */
export interface ScraperSourceCreate {
  jurisdiction: string;
  url: string;
  css_selector_scope?: string;
}

/** 202 job row from POST /api/scraper/run (global monitor run). */
export interface ScrapeRunAccepted {
  /** Job id — follow it via /api/jobs/{id}/events. */
  id: string;
}

export const REVIEW_STATUSES = ["open", "acknowledged", "dismissed"] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

/**
 * One detected change awaiting human review
 * (GET /api/scraper/review-items). Monitor-only posture: istari never
 * auto-updates rules from these — acknowledging is a recorded human act.
 */
export interface ReviewItem {
  id: string;
  source_id: string;
  /** Epoch ms when the change was detected. */
  detected_ms: number;
  /** Content hash before the change; null on the first observation. */
  prev_hash: string | null;
  new_hash: string;
  /** Human-readable change summary (e.g. "content hash changed"). */
  diff_summary: string;
  status: ReviewStatus;
  /** Reviewer notes recorded on acknowledge/dismiss. */
  notes: string | null;
  created_at: number;
  updated_at: number;
}

/**
 * PATCH /api/scraper/review-items/{id} — omitted keys are left unchanged
 * (the server treats null notes as "unchanged", so omit rather than null).
 */
export interface ReviewItemPatch {
  status?: ReviewStatus;
  notes?: string;
}

export const FINDING_SEVERITIES = ["error", "warning", "info"] as const;
export type FindingSeverity = (typeof FINDING_SEVERITIES)[number];

/** Rule citation — section numbers only, never code prose. */
export interface FindingCitation {
  /** e.g. "NFPA 72". */
  document: string;
  /** e.g. "2025". */
  edition: string | null;
  /** e.g. "17.6.3.1". */
  section: string;
}

/** One finding from GET /api/mep-systems/{id}/findings. */
export interface ApiFinding {
  id: string;
  severity: FindingSeverity;
  rule_id: string | null;
  citation: FindingCitation | null;
  /** "rule-checked: ..." message with measured vs required. */
  message: string;
  /** Measured value rendered beside the required threshold (Endra pattern). */
  measured: string | number | null;
  required: string | number | null;
  space_id: string | null;
  device_id: string | null;
  overridden: boolean;
  override_reason: string | null;
}
